import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useAppSelector } from "../../../../hooks/store-hooks";
import { IClienteTable, IIPPermitido, ITipoUsuario } from "../../../../../application/models";
import { useIPPermitidoService } from '../../../../../use-cases/ip-permitido'
import { useClientesService } from '../../../../../use-cases/clientes'
import { useVendedorService } from '../../../../../use-cases/vendedor';
import { Autocomplete, Box, Button, darken, debounce, Divider, Grid, IconButton, lighten, styled, TextField, Typography } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { format } from "date-fns";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import { SkeletonVelesTable } from "../../../../components";
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import { buildClienteTextSearchFiltersFromTerm } from '../../../../../utils/cliente-busca-unificada'

export const ListaIPPermitido = () => {
    const { currentUser } = useAppSelector((state) => state.user);
    const {
        ipPermitidoList,
        ipPermitidoTotal,
        clienteSelecionado,
    } = useAppSelector((state) => state.ipPermitido)
    const { clientes } = useAppSelector((state) => state.clientes)

    const [loading, setLoading] = useState(true)
    const [clientesCarregados, setClientesCarregados] = useState(false)
    const [buscandoClientes, setBuscandoClientes] = useState(false)
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5
    })
    const [searchInput, setSearchInput] = useState('')
    const [activeSearch, setActiveSearch] = useState('')
    const [autocompleteValue, setAutocompleteValue] = useState<IClienteTable | null>(null)

    const isVelesUser = currentUser?.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE
    const isVendedor = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL
    const isSupervisorComercial = currentUser?.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL
    const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
    const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
    const isCliente = !isVelesUser && !isVendedor && !isSupervisorComercial

    const canCreate = isDemais || isTecnologiaDaInformacao || isCliente

    const isInitialized = useRef(false)
    const lastUserIdRef = useRef<number | null>(null)
    const resetExecutedRef = useRef<boolean>(false)
    const shouldLoadIPsRef = useRef<boolean>(false)
    const paginationInitialized = useRef<boolean>(false)
    const lastFetchedClienteIdRef = useRef<number | null>(null)
    const clientesInitializedRef = useRef<boolean>(false)

    const {
        setClienteSelecionado,
        getIPPermitido,
        resetClienteSelecionado,
        setIPPermitidoPagination,
        setInsertIPPermitidoOpenForm,
        setUpdateIPPermitidoOpenForm,
        resetIPPermitidoClientes,
        resetIPPermitido
    } = useIPPermitidoService()

    const { setClientes, setClientesPagination } = useClientesService()
    const { setVendedores } = useVendedorService()


    useEffect(() => {
        if (!paginationInitialized.current) {
            setIPPermitidoPagination({
                page: 1,
                limit: 5
            });
            paginationInitialized.current = true;
        }
    }, [setIPPermitidoPagination]);

    useEffect(() => {
        if (!clienteSelecionado && ipPermitidoList && ipPermitidoList.length > 0) {
            resetIPPermitido();
        }
    }, [clienteSelecionado, ipPermitidoList, resetIPPermitido]);

    const filteredIPList = useMemo(() => {
        if (currentUser && currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE) {
            if (!clienteSelecionado) {
                return [];
            }
        }

        if (!ipPermitidoList) return [];

        if (!activeSearch.trim()) return ipPermitidoList;

        const searchTerm = activeSearch.toLowerCase();

        return ipPermitidoList.filter(ip => {
            return ip.IP.toLowerCase().includes(searchTerm);
        });
    }, [ipPermitidoList, activeSearch, currentUser, clienteSelecionado]);

    const filteredTotal = useMemo(() => {
        if (currentUser && currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE) {
            if (!clienteSelecionado) {
                return 0;
            }
        }

        if (!activeSearch.trim()) return ipPermitidoTotal || 0;
        return filteredIPList.length;
    }, [activeSearch, ipPermitidoTotal, filteredIPList.length, currentUser, clienteSelecionado]);

    useEffect(() => {
        setSearchInput('');
        setActiveSearch('');
        setPagination({ pageIndex: 0, pageSize: 5 });
    }, [clienteSelecionado]);

    useEffect(() => {
        if (!currentUser || isInitialized.current) return;

        if (currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE &&
            currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_VELES_COMERCIAL) {
            setVendedores();
        }
    }, [setVendedores, currentUser]);

    const getClientFiltersByProfile = useCallback(() => {
        if (!currentUser) return null;

        switch (currentUser.IdVelesPerfilInterno) {
            case ITipoUsuario.USUARIO_VELES_COMERCIAL:
                return {
                    SearchCNPJ: '',
                    SearchRazaoSocial: '',
                    SearchNomeFantasia: '',
                    SearchLoginCliente: '',
                    SearchInclusaoMaxima: undefined,
                    SearchInclusaoMinima: undefined,
                    SearchIdUsuario: currentUser.IdUsuario,
                    searchIdVendedor: currentUser.IdUsuario,
                    SearchIdCliente: 0
                };

            case ITipoUsuario.USUARIO_CLIENTE:
                return {
                    SearchCNPJ: '',
                    SearchRazaoSocial: '',
                    SearchNomeFantasia: '',
                    SearchLoginCliente: '',
                    SearchInclusaoMaxima: undefined,
                    SearchInclusaoMinima: undefined,
                    SearchIdUsuario: currentUser.IdUsuario,
                    searchIdVendedor: 0,
                    SearchIdCliente: currentUser.IdCliente
                };

            case ITipoUsuario.VELES_SUPERVISOR_COMERCIAL:
            case ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE:
            case ITipoUsuario.USUARIO_VELES_DEMAIS:
            case ITipoUsuario.TECNOLOGIA_DA_INFORMACAO:
                return {
                    SearchCNPJ: '',
                    SearchRazaoSocial: '',
                    SearchNomeFantasia: '',
                    SearchLoginCliente: '',
                    SearchInclusaoMaxima: undefined,
                    SearchInclusaoMinima: undefined,
                    SearchIdUsuario: currentUser.IdUsuario,
                    searchIdVendedor: 0,
                    SearchIdCliente: 0
                };

            default:
                return null;
        }
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) return;

        const isClientePerfil = currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE;
        const isSameUser = lastUserIdRef.current === currentUser.IdUsuario;
        const shouldReset = !isClientePerfil && (!isSameUser || !resetExecutedRef.current);
        const isNewUser = lastUserIdRef.current !== currentUser.IdUsuario;

        if (shouldReset || isNewUser) {
            resetIPPermitido();
            resetClienteSelecionado();
            resetIPPermitidoClientes();

            setSearchInput('');
            setActiveSearch('');
            setPagination({ pageIndex: 0, pageSize: 5 });
            setClientesCarregados(false);
            setAutocompleteValue(null);

            shouldLoadIPsRef.current = false;
            resetExecutedRef.current = true;
            clientesInitializedRef.current = false; 
            clienteAutoSelectedRef.current = false; 
            if (shouldReset) {
                setLoading(true);
            }
        }

        lastUserIdRef.current = currentUser.IdUsuario;

        if (isNewUser || !isInitialized.current) {
            isInitialized.current = true;
            clientesInitializedRef.current = false; 

            if (isClientePerfil) {
                resetExecutedRef.current = false;
                shouldLoadIPsRef.current = true;
            }

            if (currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE &&
                currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_VELES_COMERCIAL) {
                setVendedores();
            }

            setClientesPagination({
                limit: 500,
                page: 1
            });

            const filters = getClientFiltersByProfile();
            if (filters && !clientesInitializedRef.current) {
                clientesInitializedRef.current = true;
                setClientes(filters);
                setClientesCarregados(true);
            }

            setTimeout(() => {
                setLoading(false);
            }, 100);
        } else {
            setLoading(false);
        }

    }, [currentUser, getClientFiltersByProfile, resetClienteSelecionado, resetIPPermitidoClientes, resetIPPermitido, setClientes, setClientesPagination, setVendedores]);

    const clienteAutoSelectedRef = useRef<boolean>(false);
    useEffect(() => {
        if (!currentUser || !clientesCarregados || !clientes?.length) return;

        if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE && !clienteSelecionado && !clienteAutoSelectedRef.current) {
            const clienteDoUsuario = clientes.find(c => c.IdCliente === currentUser.IdCliente);

            if (clienteDoUsuario) {
                clienteAutoSelectedRef.current = true;
                const { IdVendedor, LoginUsuarioVendedor, TotalLinhas, ...clienteData } = clienteDoUsuario;
                setClienteSelecionado({
                    clienteSelecionado: clienteData
                });
            }
        }
        
        if (!clienteSelecionado) {
            clienteAutoSelectedRef.current = false;
        }
    }, [currentUser, clientesCarregados, clientes, clienteSelecionado, setClienteSelecionado]);

    useEffect(() => {
        if (clienteSelecionado) {
            const clienteCompleto = clientes.find(c => c.IdCliente === clienteSelecionado.IdCliente);
            setAutocompleteValue(clienteCompleto || null);
        } else {
            setAutocompleteValue(null);
        }
    }, [clienteSelecionado, clientes]);

    useEffect(() => {
        if (!clienteSelecionado) {
            lastFetchedClienteIdRef.current = null;
            return;
        }
        const isClientePerfil = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE;
        if (!isClientePerfil && !resetExecutedRef.current) return;

        const idCliente = clienteSelecionado.IdCliente;
        if (lastFetchedClienteIdRef.current === idCliente) return;
        lastFetchedClienteIdRef.current = idCliente;
        shouldLoadIPsRef.current = true;

        const timer = setTimeout(() => {
            getIPPermitido();
        }, 150);

        return () => clearTimeout(timer);
    }, [clienteSelecionado, getIPPermitido, currentUser]);

    useEffect(() => {
        if (!clienteSelecionado && shouldLoadIPsRef.current) {
            resetIPPermitido();
            shouldLoadIPsRef.current = false;
        }
    }, [clienteSelecionado, resetIPPermitido]);

    useEffect(() => {
        if (paginationInitialized.current) {
            setIPPermitidoPagination({
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize
            });
        }
    }, [pagination.pageIndex, pagination.pageSize, setIPPermitidoPagination]);



    const debounceSearchTerm = useMemo(() =>
        debounce(async (searchTerm: string) => {
            if (!currentUser) return;

            const termoLimpo = searchTerm.trim();

            if (!termoLimpo) {
                const baseFilters = getClientFiltersByProfile();
                if (baseFilters) {
                    setBuscandoClientes(false);
                    await setClientes(baseFilters);
                }
                return;
            }

            setBuscandoClientes(true);

            const baseFilters = getClientFiltersByProfile();
            if (!baseFilters) {
                setBuscandoClientes(false);
                return;
            }

            const filtersWithSearch = {
                ...baseFilters,
                ...buildClienteTextSearchFiltersFromTerm(termoLimpo),
            };

            try {
                await setClientes(filtersWithSearch);
            } catch (error) {
                console.error('Erro ao buscar clientes:', error);
            } finally {
                setBuscandoClientes(false);
            }
        }, 400)
        , [currentUser, getClientFiltersByProfile, setClientes]);

    const handleSearchClick = () => {
        if (clienteSelecionado && searchInput.trim()) {
            setActiveSearch(searchInput);
            setPagination(prev => ({ ...prev, pageIndex: 0 }));
        } else if (!searchInput.trim() && activeSearch) {
            setActiveSearch('');
            setPagination(prev => ({ ...prev, pageIndex: 0 }));
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && clienteSelecionado && searchInput.trim()) {
            handleSearchClick();
        }
    };

    const GroupHeader = styled('div')(({ theme }) => ({
        position: 'sticky',
        top: '-8px',
        padding: '4px 10px',
        color: theme.palette.primary.main,
        backgroundColor:
            theme.palette.mode === 'light'
                ? lighten(theme.palette.primary.light, 0.85)
                : darken(theme.palette.primary.main, 0.8),
    }));

    const GroupItems = styled('ul')({
        padding: 0,
    });

    const getGroupKey = useCallback((option: IClienteTable) =>
        (option.LoginCliente?.[0] || '').toUpperCase(), []);

    const getClienteAutocompleteLabel = useCallback(
        (option: IClienteTable) => `${option.RazaoSocial} - ${option.LoginCliente}`,
        []
    );

    const filteredClientes = useMemo(() => {
        if (!clientes || !currentUser) return [];

        let filtered: IClienteTable[] = [];

        if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE) {
            filtered = clientes.filter(c => c.IdCliente === currentUser.IdCliente);
        } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL) {
            filtered = clientes.filter(c => c.IdVendedor === currentUser.IdUsuario);
        } else {
            filtered = [...clientes];
        }

        return filtered.sort((a, b) => {
            const groupA = getGroupKey(a);
            const groupB = getGroupKey(b);
            if (groupA !== groupB) return groupA.localeCompare(groupB);
            const labelA = getClienteAutocompleteLabel(a);
            const labelB = getClienteAutocompleteLabel(b);
            return (labelA || '').localeCompare(labelB || '');
        });
    }, [clientes, currentUser, getGroupKey, getClienteAutocompleteLabel]);


    const columns = useMemo<MRT_ColumnDef<IIPPermitido>[]>(() => {
        if (!currentUser) return [];

        const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS;
        const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE;
        const canEdit = isDemais || isCliente;

        return [
            {
                header: canEdit ? 'Editar' : 'Visualizar',
                size: 50,
                Cell: ({ row }) => (
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                            setUpdateIPPermitidoOpenForm({
                                open: true,
                                ativo: row.original.Ativo,
                                idIPPermitido: row.original.IdIPPermitido,
                                ip: row.original.IP
                            })
                        }}
                    >
                        {canEdit ? <EditIcon /> : <VisibilityIcon />}
                    </IconButton>
                ),
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
            },
            {
                accessorKey: 'IP',
                header: 'IP',
                size: 150,
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
            },
            {
                accessorKey: 'Inclusao',
                header: 'Data de Inclusão',
                size: 150,
                accessorFn: (rowData) => new Date(rowData.Inclusao),
                Cell: ({ cell }) => <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>,
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' }
            },
            {
                accessorKey: 'Atualizacao',
                header: 'Data de Atualização',
                size: 150,
                accessorFn: (rowData) => rowData.Atualizacao ? new Date(rowData.Atualizacao) : null,
                Cell: ({ cell }) => {
                    if (cell.row.original.Atualizacao) {
                        return <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>;
                    }
                    return null;
                },
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
            },
            {
                accessorKey: 'Ativo',
                header: 'Status',
                size: 100,
                accessorFn: (rowData) => rowData.Ativo ? 'Ativo' : 'Inativo',
                Cell: ({ cell }) => {
                    const isActive = cell.getValue<string>() === 'Ativo';
                    return (
                        <Typography
                            variant='caption'
                            style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                backgroundColor: isActive ? 'green' : 'red',
                                color: 'white',
                                textAlign: 'center'
                            }}
                        >
                            {isActive ? 'Ativo' : 'Inativo'}
                        </Typography>
                    );
                },
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
            }
        ];
    }, [currentUser, setUpdateIPPermitidoOpenForm]);

    const table = useMaterialReactTable({
        columns,
        data: filteredIPList,
        enableHiding: false,
        enableFullScreenToggle: false,
        enableGlobalFilter: false,
        enableColumnFilters: false,
        enableExpandAll: false,
        enableDensityToggle: false,
        localization: MRT_Localization_PT_BR,
        onPaginationChange: setPagination,
        state: {
            pagination,
            isLoading: loading || !clientesCarregados
        },
        rowCount: filteredIPList.length,
        manualPagination: false
    });

    if (loading) {
        return <SkeletonVelesTable />;
    }

    if (!currentUser) {
        return (
            <Typography color="error" variant="h6" sx={{ mt: 4 }}>
                Usuário não autenticado.
            </Typography>
        );
    }

    const isNonClientWithIPs =
        currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE &&
        !clienteSelecionado &&
        ipPermitidoList &&
        ipPermitidoList.length > 0;

    if (isNonClientWithIPs) {
        resetIPPermitido();
        return <SkeletonVelesTable />;
    }

    return (
        <>
            <Typography color="primary" fontWeight="bold" variant="h6" gutterBottom>
                {isCliente ? 'IPs Permitidos da sua empresa' : 'IPs Permitidos'}
            </Typography>
            <Divider style={{ margin: '16px 0', borderBottomWidth: '2px', borderBottomColor: 'rgba(0,0,0,0.5)' }} />

            <Grid container alignItems="center" mb={2}>
                {canCreate && (
                    <Grid item xs={2}>
                        <Button
                            disabled={!clienteSelecionado}
                            variant="contained"
                            color="primary"
                            onClick={() => setInsertIPPermitidoOpenForm({ open: true })}
                        >
                            <AddIcon style={{ marginRight: '8px' }} /> Inserir novo IP
                        </Button>
                    </Grid>
                )}

                <Grid item xs={canCreate ? 10 : 12}>
                    <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
                        {isCliente ? (
                            <>
                                <Grid item xs={8}>
                                    <TextField
                                        fullWidth
                                        label="Buscar por IP"
                                        placeholder="Digite um IP para buscar (com ou sem pontos)..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={!clienteSelecionado}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <Button
                                        disabled={!clienteSelecionado || !searchInput.trim()}
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSearchClick}
                                    >
                                        <SearchIcon /> Buscar
                                    </Button>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs={4}>
                                    <Autocomplete
                                        fullWidth
                                        autoComplete
                                        includeInputInList
                                        options={filteredClientes}
                                        loading={buscandoClientes}
                                        groupBy={getGroupKey}
                                        getOptionLabel={getClienteAutocompleteLabel}
                                        filterOptions={(options) => options}
                                        renderOption={(props, option) => {
                                            const label = getClienteAutocompleteLabel(option);

                                            return (
                                                <li {...props} key={option.IdCliente}>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.CNPJ}{option.NomeFantasia ? ` · ${option.NomeFantasia}` : ''}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                        renderGroup={(params) => (
                                            <li key={params.key}>
                                                <GroupHeader>{params.group}</GroupHeader>
                                                <GroupItems>{params.children}</GroupItems>
                                            </li>
                                        )}
                                        onInputChange={(event, value, reason) => {
                                            if (reason === 'input') {
                                                debounceSearchTerm(value);
                                            }
                                        }}
                                        onChange={(event: any, newValue: IClienteTable | null) => {
                                            setAutocompleteValue(newValue);

                                            if (newValue) {
                                                resetIPPermitido();
                                                const { IdVendedor, LoginUsuarioVendedor, TotalLinhas, ...clienteData } = newValue;
                                                setClienteSelecionado({
                                                    clienteSelecionado: clienteData
                                                });
                                                shouldLoadIPsRef.current = true;
                                            } else {
                                                resetClienteSelecionado();
                                                resetIPPermitido();
                                                shouldLoadIPsRef.current = false;
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={
                                                    isCliente ? "Sua empresa" :
                                                        isVendedor ? "Seus clientes" :
                                                            "Selecione um cliente"
                                                }
                                                placeholder="Login, razão social, nome fantasia ou CNPJ"
                                                helperText={buscandoClientes ? "Buscando clientes..." : ""}
                                            />
                                        )}
                                        disabled={isCliente}
                                        value={autocompleteValue}
                                        noOptionsText={
                                            buscandoClientes
                                                ? "Buscando..."
                                                : filteredClientes.length === 0
                                                    ? "Nenhum cliente encontrado para o termo informado"
                                                    : "Nenhum cliente encontrado"
                                        }
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        label="Buscar por IP"
                                        placeholder="Digite um IP para buscar (com ou sem pontos)..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={!clienteSelecionado}
                                    />
                                </Grid>

                                <Grid item xs={2}>
                                    <Button
                                        disabled={!clienteSelecionado || !searchInput.trim()}
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSearchClick}
                                    >
                                        <SearchIcon /> Buscar
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <MaterialReactTable table={table} />
        </>
    );
}